import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo } from "react";

import { IgAzurePrimitives } from "@intelligems/ig-design-tokens/constants";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { DataType } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { useSelector } from "@src/store";
import { getHasAnyUpsellBlocks } from "@src/store/slices/checkoutBlocks/selectors";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";

import { columnConfig } from "./productLevelDetailConfig";

interface ProductGroupsTableProps {
  data: any[] | undefined;
  isLoading: boolean;
  variantSelection?: string;
}

const getUpsellSource = (
  experience: ReturnType<typeof selectedExperience>,
  hasUpsellBlocks: boolean
) => {
  if (experience.testTypes.hasTestPostPurchase) {
    return "postPurchase" as const;
  }

  if (hasUpsellBlocks) {
    return "checkout" as const;
  }

  return undefined;
};

const MARGIN_WITH_VARIANTS = "75px";
const MARGIN_WITHOUT_VARIANTS = "50px";
const NO_MARGIN = "0px";

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .ag-header-cell:first-of-type`]: {
    borderRight: `${theme.igBorders.core.default}`,
  },
  [`& .ag-header-row`]: {
    backgroundColor: IgAzurePrimitives[100],
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
  },
  [`& .ag-header-cell-text`]: {
    color: `${theme.palette.text.secondary} !important`,
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "18px",
    letterSpacing: ".25px",
  },
  [`& .ag-cell:first-of-type`]: {
    borderRight: `1px solid ${theme.igPrimitives.gray[600]}`,
    fontSize: "10px",
  },
  [`& .ag-cell-numeric`]: {
    [`&.ag-cell`]: {
      textAlign: "center",
      fontSize: "14px",
      lineHeight: "16.8px",
      [`&.ag-cell:not(:last-child)`]: {
        borderRight: `1px solid ${theme.igPrimitives.gray[600]}`,
      },
    },
  },
  [`& .ag-cell-wrapper.ag-row-group`]: {
    alignItems: "center !important",
  },
  [`& .ag-center-cols-viewport`]: {
    minHeight: "10px !important",
  },
}));

export const ProductLevelDetailTable = ({
  data,
  variantSelection,
  isLoading,
}: ProductGroupsTableProps) => {
  const experience = useSelector(selectedExperience);
  const graphValueFormatter = useGraphValueFormatter();
  const getFormattedMetricValue = useCallback(
    (value: number, metricDataType: DataType | "noFormatting") => {
      if (!value) {
        return "-";
      }
      if (metricDataType === "noFormatting") {
        return value;
      }
      return graphValueFormatter({
        isAxisTick: false,
        value: value || 0,
        configDataTypes: [metricDataType],
      });
    },
    [data]
  );
  const variations = useSelector(selectedVariations);
  const hasUpsellBlocks = useSelector(getHasAnyUpsellBlocks());
  const upsellSource = getUpsellSource(experience, hasUpsellBlocks);
  const rows = useMemo(() => {
    if (!data || !data.length) {
      return [];
    }
    return data.map((metric) => {
      return {
        ...metric,
        variation_id: variations.find(
          (variation) => variation.id === metric.variation_id
        )?.name,
      };
    });
  }, [data]);
  const columnDefs: ColDef[] = useMemo(() => {
    return columnConfig
      .filter((col) => {
        if (col.renderRule) {
          return col.renderRule({
            variantSelection: variantSelection,
            upsellSource,
          });
        }
        return true;
      })
      .map((config) => {
        return {
          flex: 1,
          field: config.key,
          headerName: config.label,
          rowGroup: config.rowGroup,
          hide: config.rowGroup,
          minWidth: config.defaultWidth ? config.defaultWidth : 175,
          maxWidth: 350,
          resizable: true,
          // eslint-disable-next-line react/no-unstable-nested-components
          headerComponent: () => (
            <Grid
              container
              sx={{
                justifyContent:
                  config.label === "Test Group" ? "flex-start" : "flex-end",
              }}
            >
              <IgTypography
                sx={{
                  fontWeight: 600,
                  display: "inline",
                  alignItems: "center",
                  textAlign: "end",
                }}
              >
                {config.label}
              </IgTypography>
            </Grid>
          ),
          cellStyle: {
            fontSize: "14px",
            alignContent: "center",
          },
          menuTabs: [],
          suppressMovable: true,
          wrapHeaderText: true,
          sortable: false,
          suppressHeaderMenuButton: true,
          dataFormat: config.dataFormat,
          // eslint-disable-next-line react/no-unstable-nested-components
          cellRenderer: (params) => {
            return (
              <Grid
                container
                width={"100%"}
                maxWidth={"150px"}
                marginLeft={
                  params.colDef.headerName === "Test Group"
                    ? variantSelection === "include_variants"
                      ? MARGIN_WITH_VARIANTS
                      : MARGIN_WITHOUT_VARIANTS
                    : NO_MARGIN
                }
                sx={
                  params.colDef.headerName !== "Test Group"
                    ? { justifyContent: "flex-end" }
                    : {}
                }
              >
                <IgTypography
                  sx={{
                    fontWeight: 400,
                    textAlign: "flex-end",
                  }}
                >
                  {getFormattedMetricValue(
                    params.value,
                    params.colDef.dataFormat
                  )}
                </IgTypography>
              </Grid>
            );
          },
        };
      });
  }, [upsellSource, variantSelection]);

  return (
    <StyledGrid
      sx={{
        // Set grid height to viewport minus sticky header elements.
        // This enables the sticky row header while keeping the grid within viewport bounds.
        // TODO: Replace magic number with dynamic calculation of actual sticky element heights.
        height: "calc(100vh - 330px)",
        marginTop: "2px",
        paddingRight: "0px !important",
        width: "100%",
      }}
    >
      <AgGridReact
        rowData={rows}
        columnDefs={columnDefs}
        rowHeight={48}
        groupDefaultExpanded={2}
        loading={isLoading}
        groupDisplayType={"groupRows"}
        suppressDragLeaveHidesColumns={true}
        suppressContextMenu={true}
        alwaysShowHorizontalScroll={true}
        alwaysShowVerticalScroll={true}
        ensureDomOrder={true}
        overlayLoadingTemplate={"<div>Loading Data... </div>"}
        overlayNoRowsTemplate={
          "<div>No Data - Your filters may have resulted in no data, try resetting them</div>"
        }
        cellSelection={{
          handle: {
            mode: "range",
          },
        }}
        suppressRowHoverHighlight={true}
        enableBrowserTooltips={true}
        enableCellTextSelection={true}
      />
    </StyledGrid>
  );
};
